import {
  LuUsers,
  LuCheck,
  LuClock,
  LuSearch,
  LuFilter,
  LuActivity,
  LuLoader,
  LuDollarSign,
  LuBus,
  LuFileSpreadsheet,
} from "react-icons/lu";
import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { db } from "@lib/firebase";
import {
  collection,
  getDocs,
  query,
  orderBy,
  limit,
  startAfter,
  where,
  getCountFromServer,
} from "firebase/firestore";
import { InputField } from "@cakrawalajakarta/shared-components";

const COLLECTION_NAME = "pendaftaranShuttle2026";
const ITEMS_PER_PAGE = 50;

export const AdminShuttle = () => {
  const { getIdToken } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [expandedRow, setExpandedRow] = useState(null);
  const [lastVisible, setLastVisible] = useState(null);
  const [isLastPage, setIsLastPage] = useState(false);
  const [stats, setStats] = useState({ total: 0, settlement: 0, pending: 0, revenue: 0, netRevenue: 0, roundTrip: 0, departureOnly: 0, returnOnly: 0 });
  const [tripTypeFilter, setTripTypeFilter] = useState("all");

  useEffect(() => {
    fetchOrders("init");
    fetchStats();
  }, [statusFilter, tripTypeFilter]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchOrders("init");
    }, 800);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const fetchStats = async () => {
    try {
      const coll = collection(db, COLLECTION_NAME);
      const [totalSnap, settlementSnap, pendingSnap, paidOrdersSnap] = await Promise.all([
        getCountFromServer(query(coll, where("transaction_status", "!=", "checkout"))),
        getCountFromServer(query(coll, where("transaction_status", "==", "settlement"))),
        getCountFromServer(query(coll, where("transaction_status", "==", "pending"))),
        getDocs(query(coll, where("transaction_status", "in", ["settlement", "capture"]))),
      ]);

      const paidOrders = paidOrdersSnap.docs.map((doc) => doc.data());
      const grossRevenue = paidOrders.reduce((sum, order) => {
        return sum + Number(order.totalHarga || order.harga || order.gross_amount || 0);
      }, 0);
      const netRevenue = Math.max(0, grossRevenue - (paidOrders.length * 4440));

      const roundTrip = paidOrders.filter(o => o.tripType === "round-trip").length;
      const departureOnly = paidOrders.filter(o => o.tripType === "one-way-departure").length;
      const returnOnly = paidOrders.filter(o => o.tripType === "one-way-return").length;

      setStats({
        total: totalSnap.data().count,
        settlement: settlementSnap.data().count,
        pending: pendingSnap.data().count,
        revenue: grossRevenue,
        netRevenue,
        roundTrip,
        departureOnly,
        returnOnly,
      });
    } catch (err) {
      console.error("fetchStats error:", err);
    }
  };

  const fetchOrders = async (action = "init") => {
    setLoading(true);
    try {
      const constraints = [];

      const shouldHideCheckout = statusFilter === "all";

      if (statusFilter !== "all") {
        const dbStatus = statusFilter === "expired" ? "expire" : statusFilter;
        constraints.push(where("transaction_status", "==", dbStatus));
      }

      if (tripTypeFilter !== "all") {
        constraints.push(where("tripType", "==", tripTypeFilter));
      }

      const term = searchQuery.trim();
      const isNameLikeSearch = term && !term.toUpperCase().startsWith("SHUTTLE-") && !term.includes("@") && !/^\d+$/.test(term);

      if (term && !isNameLikeSearch) {
        if (term.toUpperCase().startsWith("SHUTTLE-")) {
          constraints.push(where("orderId", "==", term));
        } else if (term.includes("@")) {
          constraints.push(where("email", "==", term));
        } else if (/^\d+$/.test(term)) {
          constraints.push(where("telepon", "==", term));
        }
      }

      constraints.push(orderBy("createdAt", "desc"));

      if (action === "next" && lastVisible && !isNameLikeSearch) {
        constraints.push(startAfter(lastVisible));
      }
      constraints.push(limit(isNameLikeSearch || shouldHideCheckout ? 300 : ITEMS_PER_PAGE));

      const snapshot = await getDocs(query(collection(db, COLLECTION_NAME), ...constraints));
      let list = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));

      if (shouldHideCheckout) {
        list = list.filter((order) => order.transaction_status !== "checkout");
      }

      if (isNameLikeSearch) {
        const lowerTerm = term.toLowerCase();
        list = list.filter((order) =>
          [
            order.namaLengkap,
            order.email,
            order.telepon,
            order.orderId,
            order.id,
          ]
            .filter(Boolean)
            .some((value) => String(value).toLowerCase().includes(lowerTerm))
        );
      }

      if (snapshot.docs.length > 0) {
        setLastVisible(snapshot.docs[snapshot.docs.length - 1]);
      }
      const visibleLimit = isNameLikeSearch || shouldHideCheckout ? 300 : ITEMS_PER_PAGE;
      if (!isNameLikeSearch && !shouldHideCheckout) {
        list = list.slice(0, ITEMS_PER_PAGE);
      }
      setIsLastPage(isNameLikeSearch || shouldHideCheckout || snapshot.empty || snapshot.docs.length < visibleLimit);

      if (action === "next") {
        setOrders((prev) => [...prev, ...list]);
      } else {
        setOrders(list);
      }
    } catch (err) {
      console.error("fetchOrders error:", err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    if (status === "settlement" || status === "capture")
      return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">Paid</span>;
    if (status === "pending")
      return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">Pending</span>;
    if (status === "checkout")
      return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">Checkout</span>;
    if (status === "expire")
      return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">Expired</span>;
    if (status === "cancel" || status === "deny" || status === "failure")
      return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">Failed</span>;
    return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">{status || "-"}</span>;
  };

  const formatCurrency = (value) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "IDR", minimumFractionDigits: 0 }).format(value || 0);

  const formatDate = (ts) => {
    if (!ts) return "-";
    const d = ts.seconds ? new Date(ts.seconds * 1000) : new Date(ts);
    return d.toLocaleString("en-US", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  const exportToGoogleSheets = async () => {
    if (!confirm("Existing data in the target Google Sheet will be overwritten. Continue?")) return;
    
    const spreadsheetId = "1TfC7vzWgJ0WhQcF-24iHMWOnmOVYNovYHzytApI8jaw";
    setExporting(true);
    
    try {
        const response = await axios.post(`${import.meta.env.VITE_API_BASE_URL}/api/export-shuttle-sheets`, {
            spreadsheetId
        }, {
            headers: {
                Authorization: `Bearer ${await getIdToken()}`
            }
        });
        
        if (response.data.success) {
            alert("Success! Opening Google Sheets...");
            window.open(response.data.url, "_blank");
        } else if (response.data.message) {
            alert(response.data.message);
        }
    } catch (error) {
        console.error("Export to Sheets failed:", error);
        alert("Failed to export to Google Sheets: " + (error.response?.data?.error || error.message));
    } finally {
        setExporting(false);
    }
  };


  if (loading && orders.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <LuLoader className="animate-spin text-4xl text-lime-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white rounded-xl p-4 border border-slate-200 text-slate-800 relative overflow-hidden">
        <div className="absolute top-0 right-0 p-4 opacity-5 pointer-events-none">
          <LuBus size={120} />
        </div>
        <div className="relative z-10">
          <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
             <div>
               <p className="text-slate-500 font-medium mb-1 flex items-center gap-2 text-xs uppercase tracking-wider">
                 <LuActivity size={14} /> Administration
               </p>
               <h1 className="text-2xl md:text-3xl font-bold mb-2 break-words">Shuttle Orders</h1>
               <p className="text-slate-500 text-sm max-w-xl">Manage JOTR 2026 shuttle orders</p>
             </div>
             <div className="flex flex-col sm:flex-row md:justify-end gap-2">
               <button
                   onClick={exportToGoogleSheets}
                   disabled={exporting}
                   className="w-full md:w-auto inline-flex justify-center items-center gap-2 bg-green-600 text-white px-4 py-3 md:py-2 rounded-xl hover:bg-green-700 transition-colors text-sm font-medium h-fit disabled:opacity-50"
               >
                   {exporting ? <LuLoader className="animate-spin" /> : <LuFileSpreadsheet className="text-white" />}
                   Google Sheets
               </button>
             </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-xl border border-slate-200 relative">
          <div className="absolute top-4 right-4 text-slate-400"><LuDollarSign size={20} /></div>
          <p className="text-slate-500 text-sm font-medium mb-1">Revenue</p>
          <h3 className="text-2xl font-bold text-slate-800">{formatCurrency(stats.revenue)}</h3>
          <p className="text-sm text-slate-400 mt-1">Net: {formatCurrency(stats.netRevenue)}</p>
        </div>
        <div className="bg-white p-4 rounded-xl border border-slate-200 relative">
          <div className="absolute top-4 right-4 text-slate-400"><LuUsers size={20} /></div>
          <p className="text-slate-500 text-sm font-medium mb-1">Total</p>
          <h3 className="text-2xl font-bold text-slate-800">{stats.total}</h3>
          <p className="text-sm text-slate-400 mt-1">All orders</p>
        </div>
        <div className="bg-white p-4 rounded-xl border border-slate-200 relative">
          <div className="absolute top-4 right-4 text-slate-400"><LuCheck size={20} /></div>
          <p className="text-slate-500 text-sm font-medium mb-1">Paid</p>
          <h3 className="text-2xl font-bold text-slate-800">{stats.settlement}</h3>
          <p className="text-sm text-slate-400 mt-1">Settlement</p>
        </div>
        <div className="bg-white p-4 rounded-xl border border-slate-200 relative">
          <div className="absolute top-4 right-4 text-slate-400"><LuClock size={20} /></div>
          <p className="text-slate-500 text-sm font-medium mb-1">Pending</p>
          <h3 className="text-2xl font-bold text-slate-800">{stats.pending}</h3>
          <p className="text-sm text-slate-400 mt-1">Awaiting payment</p>
        </div>
      </div>

      {/* Trip Type Breakdown */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white p-3 rounded-xl border border-slate-200">
          <p className="text-slate-500 text-xs font-medium mb-1">Round Trip (PP)</p>
          <h3 className="text-xl font-bold text-slate-800">{stats.roundTrip}</h3>
          <p className="text-xs text-slate-400">@ IDR 300,000</p>
        </div>
        <div className="bg-white p-3 rounded-xl border border-slate-200">
          <p className="text-slate-500 text-xs font-medium mb-1">Departure Only</p>
          <h3 className="text-xl font-bold text-slate-800">{stats.departureOnly}</h3>
          <p className="text-xs text-slate-400">@ IDR 200,000</p>
        </div>
        <div className="bg-white p-3 rounded-xl border border-slate-200">
          <p className="text-slate-500 text-xs font-medium mb-1">Return Only</p>
          <h3 className="text-xl font-bold text-slate-800">{stats.returnOnly}</h3>
          <p className="text-xs text-slate-400">@ IDR 200,000</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 border border-slate-200">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <InputField
              name="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name, email, Order ID, or phone number..."
              startIcon={<LuSearch className="text-slate-400" />}
              labelClassName="sr-only"
            />
          </div>
          <div className="min-w-[160px]">
            <InputField
              type="select"
              name="tripTypeFilter"
              value={tripTypeFilter}
              onChange={(e) => { setTripTypeFilter(e.target.value); setLastVisible(null); }}
              options={[
                { value: "all", label: "All Trip Types" },
                { value: "round-trip", label: "Round Trip (PP)" },
                { value: "one-way-departure", label: "Departure Only" },
                { value: "one-way-return", label: "Return Only" },
              ]}
              startIcon={<LuBus className="text-slate-400" />}
              labelClassName="sr-only"
            />
          </div>
          <div className="min-w-[160px]">
            <InputField
              type="select"
              name="statusFilter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              options={[
                { value: "all", label: "All Statuses" },
                { value: "settlement", label: "Paid" },
                { value: "pending", label: "Pending" },
                { value: "checkout", label: "Checkout" },
                { value: "expired", label: "Expired" },
              ]}
              startIcon={<LuFilter className="text-slate-400" />}
              labelClassName="sr-only"
            />
          </div>
        </div>
      </div>

      <p className="text-sm text-slate-500">
        Showing <span className="font-semibold text-slate-700">{orders.length}</span> orders
      </p>

      {/* Orders List */}
      <div className="space-y-3">
        {orders.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
            <p className="text-slate-500">No orders found</p>
          </div>
        ) : (
          orders.map((order) => (
            <div key={order.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div
                className="p-4 cursor-pointer hover:bg-slate-50 transition-colors"
                onClick={() => setExpandedRow(expandedRow === order.id ? null : order.id)}
              >
                {/* Mobile */}
                <div className="lg:hidden flex flex-col gap-2">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-slate-900 text-base truncate">{order.namaLengkap}</p>
                      <p className="text-sm text-slate-500 truncate">{order.email}</p>
                    </div>
                    {getStatusBadge(order.transaction_status)}
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                    <span className="font-mono">{order.orderId || order.id}</span>
                    <span>{formatCurrency(order.harga || order.gross_amount)}</span>
                    <span>{formatDate(order.createdAt)}</span>
                  </div>
                </div>

                {/* Desktop */}
                <div className="hidden lg:grid grid-cols-12 gap-4 items-center">
                  <div className="col-span-3">
                    <p className="font-semibold text-slate-800 text-sm truncate">{order.namaLengkap}</p>
                    <p className="text-xs text-slate-500 truncate">{order.email}</p>
                  </div>
                  <div className="col-span-3">
                    <p className="text-xs text-slate-400 mb-0.5">Order ID</p>
                    <p className="text-sm font-mono text-slate-700">{order.orderId || order.id}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-xs text-slate-400 mb-0.5">Route & Type</p>
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${
                        order.tripType === "round-trip" ? "bg-lime-100 text-lime-700" :
                        order.tripType === "one-way-departure" ? "bg-blue-100 text-blue-700" :
                        order.tripType === "one-way-return" ? "bg-purple-100 text-purple-700" : "bg-slate-100 text-slate-600"
                      }`}>
                        {order.tripType === "round-trip" ? "PP" : order.tripType === "one-way-departure" ? "D" : order.tripType === "one-way-return" ? "R" : "-"}
                      </span>
                      <div className="text-sm text-slate-700 truncate" title={`Departure: ${order.pickupPoint || "-"}\nReturn: ${order.returnPoint || "-"}`}>
                        {[
                          order.pickupPoint && order.pickupPoint !== "-" ? `D: ${order.pickupPoint.split('/')[0]}` : null,
                          order.returnPoint && order.returnPoint !== "-" ? `R: ${order.returnPoint.split('/')[0]}` : null,
                        ].filter(Boolean).join(" • ") || "-"}
                      </div>
                    </div>
                  </div>
                  <div className="col-span-2 text-right">
                    <p className="text-sm font-bold text-slate-800">{formatCurrency(order.harga || order.gross_amount)}</p>
                    <p className="text-xs text-slate-400">{formatDate(order.createdAt)}</p>
                  </div>
                  <div className="col-span-2 flex justify-end">
                    {getStatusBadge(order.transaction_status)}
                  </div>
                </div>
              </div>

              {/* Expanded Detail */}
              {expandedRow === order.id && (
                <div className="border-t border-slate-200 bg-slate-50 p-4">
                  <div className="bg-white rounded-xl p-4 border border-slate-200">
                    <h4 className="text-sm font-semibold text-slate-700 mb-3 pb-2 border-b border-slate-200">Order Details</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {[
                        { label: "Order ID", value: order.orderId || order.id },
                        { label: "Full Name", value: order.namaLengkap },
                        { label: "Email", value: order.email },
                        { label: "Phone", value: order.telepon },
                        { label: "Gender", value: order.gender },
                        { label: "Address", value: order.alamat },
                        { label: "Trip Type", value: order.tripType === "round-trip" ? "Round Trip (PP)" : order.tripType === "one-way-departure" ? "Departure Only" : order.tripType === "one-way-return" ? "Return Only" : order.tripType },
                        { label: "Pickup Point", value: order.tripType !== "one-way-return" ? order.pickupPoint : "-" },
                        { label: "Departure Time", value: order.tripType !== "one-way-return" ? order.departureTime : "-" },
                        { label: "Return Point", value: order.tripType !== "one-way-departure" ? order.returnPoint : "-" },
                        { label: "Return Time", value: order.tripType !== "one-way-departure" ? order.returnTime : "-" },
                        { label: "Payment Type", value: order.payment_type },
                        { label: "Total", value: formatCurrency(order.harga || order.gross_amount) },
                        { label: "Status", value: order.transaction_status },
                        { label: "Created At", value: formatDate(order.createdAt) },
                      ].map(({ label, value }) => (
                        <div key={label}>
                          <p className="text-slate-500 text-xs uppercase mb-1">{label}</p>
                          <p className="text-slate-800 text-sm font-medium break-words">{value || "-"}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Load More */}
      {!isLastPage && (
        <div className="flex justify-end mt-4">
          <button
            onClick={() => fetchOrders("next")}
            disabled={loading}
            className="px-6 py-2 rounded-xl bg-slate-800 text-white hover:bg-slate-900 disabled:opacity-50 transition-all flex items-center gap-2"
          >
            {loading ? <LuLoader className="animate-spin" /> : null}
            Load More
          </button>
        </div>
      )}
    </div>
  );
};
